import { Controller } from '@hotwired/stimulus'

const MINIMUM_THINKING_TIME_MS = 1400

export default class extends Controller {
  static targets = ['form', 'input', 'messages', 'submit', 'conversation']
  static values = {
    errorMessage: String,
    thinkingMessage: String
  }

  connect() {
    this.busy = false
    this.scrollToLatest()
  }

  keydown(event) {
    if(event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.formTarget.requestSubmit()
    }
  }

  async submit(event) {
    event.preventDefault()
    const message = this.inputTarget.value.trim()
    if(this.busy || message.length === 0) return

    this.busy = true
    this.submitTarget.disabled = true
    this.inputTarget.disabled = true
    const userMessage = this.appendMessage('user', message)
    const thinkingStartedAt = Date.now()
    const thinkingMessage = this.appendThinkingMessage()
    this.inputTarget.value = ''
    let errorMessage = this.errorMessageValue

    try {
      const response = await fetch(this.formTarget.action, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          message: message,
          conversation_id: this.conversationTarget.value
        })
      })
      const payload = await this.responsePayload(response)
      await this.waitForMinimumThinkingTime(thinkingStartedAt)
      thinkingMessage.remove()
      if(!response.ok) {
        errorMessage = payload.error || errorMessage
        throw new Error('Chat request failed')
      }
      if(typeof payload.answer !== 'string' || !payload.conversation_id) {
        throw new Error('Invalid chat response')
      }

      this.conversationTarget.value = payload.conversation_id
      this.appendMessage('assistant', payload.answer)
      this.rememberConversation(payload.conversation_id)
    } catch(_error) {
      await this.waitForMinimumThinkingTime(thinkingStartedAt)
      thinkingMessage.remove()
      // Al fallar se deshace el envío: la pregunta vuelve al campo y desaparece del hilo,
      // que solo debe guardar lo que llegó a contestarse. Errores como el de asistente
      // ocupado piden reintentar, y reescribirlo entero sería un castigo de más.
      userMessage.remove()
      this.inputTarget.value = message
      this.appendMessage('error', errorMessage)
    } finally {
      this.busy = false
      this.submitTarget.disabled = false
      this.inputTarget.disabled = false
      this.inputTarget.focus()
    }
  }

  waitForMinimumThinkingTime(startedAt) {
    const remainingTime = Math.max(0, MINIMUM_THINKING_TIME_MS - (Date.now() - startedAt))
    return new Promise(resolve => window.setTimeout(resolve, remainingTime))
  }

  appendThinkingMessage() {
    const article = document.createElement('article')
    article.className = 'aim-chat-message assistant thinking'
    article.setAttribute('role', 'status')

    const clock = document.createElement('span')
    clock.className = 'aim-chat-thinking-clock'
    clock.setAttribute('aria-hidden', 'true')

    const text = document.createElement('span')
    text.textContent = this.thinkingMessageValue
    article.append(clock, text)
    this.messagesTarget.appendChild(article)
    this.scrollToLatest()
    return article
  }

  async responsePayload(response) {
    const contentType = response.headers.get('content-type') || ''
    if(!contentType.includes('application/json')) return {}

    try {
      return await response.json()
    } catch(_error) {
      return {}
    }
  }

  appendMessage(role, content) {
    const article = document.createElement('article')
    article.className = `aim-chat-message ${role}`
    const paragraph = document.createElement('p')
    paragraph.textContent = content
    article.appendChild(paragraph)
    this.messagesTarget.appendChild(article)
    this.scrollToLatest()
    return article
  }

  scrollToLatest() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  rememberConversation(id) {
    const url = new URL(window.location.href)
    url.searchParams.set('conversation_id', id)
    window.history.replaceState({}, '', url.toString())
  }
}
